import PropTypes from 'prop-types';
import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Routes, Route, useNavigate } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { layoutTypes } from "./constants/layout";

// Import Routes
import { authProtectedRoutes, publicRoutes } from "./routes";

// Import middleware
import Authmiddleware from "./routes/route";

// Layouts
import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

// Import scss
import "./assets/scss/theme.scss";

// Import fake backend
import fakeBackend from "./helpers/AuthType/fakeBackend";

// Activating fake backend for non-auth0 paths
fakeBackend();

// Timer constant (in milliseconds)
const TIMER_DURATION = 12 * 60 * 60 * 1000; 

const getLayout = (layoutType) => {
  let Layout = VerticalLayout;
  switch (layoutType) {
    case layoutTypes.VERTICAL:
      Layout = VerticalLayout;
      break;
    case layoutTypes.HORIZONTAL:
      Layout = HorizontalLayout;
      break;
    default:
      break;
  }
  return Layout;
};

// Wrapper component to provide Auth0 context
const Auth0ProviderWithHistory = ({ children }) => {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
      }}
    >
      {children}
    </Auth0Provider>
  );
};

const App = () => {
  const LayoutProperties = createSelector(
    (state) => state.Layout,
    (layout) => ({
      layoutType: layout.layoutType,
    })
  );

  const { layoutType } = useSelector(LayoutProperties);
  const Layout = getLayout(layoutType);
  const navigate = useNavigate();

  // Use useRef to persist the timer reference across re-renders
  const timerRef = useRef(null);

  // Function to handle timeout logic (user inactive)
  const handleTimeout = () => {
    localStorage.clear();
    localStorage.setItem('logoutRedirected', 'true');
    navigate('/logout');
  };

  // Reset the timer on user activity (click/keydown)
  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);  // Clear the existing timer
    }
    timerRef.current = setTimeout(handleTimeout, TIMER_DURATION);  // Set a new timeout
  };

  // Check localStorage for pre-existing session and set the logout behavior
  useEffect(() => {
    const timerSet = localStorage.getItem('hasTimerSet');
    const logoutRedirected = localStorage.getItem('logoutRedirected');

    // If there's no timer set and user hasn't been redirected to logout, log them out immediately
    if (!timerSet && !logoutRedirected) {
      localStorage.clear();
      localStorage.setItem('logoutRedirected', 'true');
      navigate('/logout');
    } else if (timerSet === 'true') {
      // Set the initial timeout (auto-logout after the specified duration)
      timerRef.current = setTimeout(handleTimeout, TIMER_DURATION);

      // Attach event listeners for user activity (click and keydown)
      window.addEventListener('click', resetTimer);
      window.addEventListener('keydown', resetTimer);

    } else {
      // Set 'hasTimerSet' when the timer is first initialized
      if (timerSet === null) {
        localStorage.setItem('hasTimerSet', 'true');
      }
    }

    // Cleanup function to clear the timer and remove event listeners
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, [navigate]);

  return (
    <Auth0ProviderWithHistory>
      <Routes>
        {/* Public Routes */}
        {publicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={<NonAuthLayout>{route.component}</NonAuthLayout>}
            key={idx}
            exact={true}
          />
        ))}

        {/* Protected Routes */}
        {authProtectedRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={<Authmiddleware><Layout>{route.component}</Layout></Authmiddleware>}
            key={idx}
            exact={true}
          />
        ))}
      </Routes>
    </Auth0ProviderWithHistory>
  );
};

App.propTypes = {
  layout: PropTypes.any
};

export default App;
